import { db } from '../services/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';

/**
 * Sistema mejorado de notificaciones para administradores
 * Detecta automáticamente todos los usuarios con rol "admin" y les envía notificaciones
 */

// Cache de IDs de administradores para evitar consultas repetidas
let adminIdsCache = [];
let adminCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Detecta automáticamente todos los usuarios con rol "admin"
 * @param {boolean} forceRefresh - Si es true, ignora la caché y realiza una nueva consulta
 * @returns {Promise<string[]>} - Array con los IDs de los administradores
 */
export const detectAdminsByRole = async (forceRefresh = false) => {
  try {
    // Usar caché si está disponible y no ha expirado
    const now = Date.now();
    if (!forceRefresh && adminIdsCache.length > 0 && adminCacheTimestamp && (now - adminCacheTimestamp < CACHE_DURATION)) {
      console.log('🔍 Usando caché de administradores:', adminIdsCache);
      return adminIdsCache;
    }

    console.log('🔍 Detectando administradores por rol...');
    const adminIds = [];

    // Buscar usuarios con role="admin" (método principal)
    const usersRef = collection(db, 'users');
    const roleQuery = query(usersRef, where('role', '==', 'admin'));
    const querySnapshot = await getDocs(roleQuery);

    querySnapshot.forEach(doc => {
      adminIds.push(doc.id);
      console.log(`✅ Administrador encontrado por role="admin": ${doc.id}`);
    });

    // Actualizar caché
    if (adminIds.length > 0) {
      adminIdsCache = [...adminIds];
      adminCacheTimestamp = now;
      console.log(`🎯 Total administradores detectados: ${adminIds.length}`);
      return adminIds;
    }

    // Si no se encontraron administradores, intentar con métodos alternativos
    console.warn('⚠️ No se encontraron administradores con role="admin"');
    
    // Método alternativo: isAdmin=true
    const isAdminQuery = query(usersRef, where('isAdmin', '==', true));
    const isAdminSnapshot = await getDocs(isAdminQuery);
    
    isAdminSnapshot.forEach(doc => {
      adminIds.push(doc.id);
      console.log(`✅ Administrador encontrado por isAdmin=true: ${doc.id}`);
    });

    // Actualizar caché si se encontraron administradores
    if (adminIds.length > 0) {
      adminIdsCache = [...adminIds];
      adminCacheTimestamp = now;
      console.log(`🎯 Total administradores detectados: ${adminIds.length}`);
      return adminIds;
    }

    // Si aún no hay administradores, usar fallback
    console.warn('⚠️ No se encontraron administradores en la base de datos');
    const fallbackAdminId = process.env.REACT_APP_FALLBACK_ADMIN_UID;
    
    if (fallbackAdminId) {
      console.log(`🔄 Usando ID de fallback: ${fallbackAdminId}`);
      adminIdsCache = [fallbackAdminId];
      adminCacheTimestamp = now;
      return [fallbackAdminId];
    }

    console.error('❌ No hay administradores disponibles para notificaciones');
    return [];
  } catch (error) {
    console.error('❌ Error detectando administradores:', error);
    return [];
  }
};

/**
 * Envía una notificación a todos los administradores
 * @param {Object} notificationData - Datos de la notificación (sin userId)
 * @param {Function} createNotificationFn - Función para crear notificaciones
 * @returns {Promise<{success: number, failed: number}>} - Resultado del envío
 */
export const notifyAllAdmins = async (notificationData, createNotificationFn) => {
  try {
    console.log('📣 Enviando notificación a todos los administradores...');
    
    // Detectar administradores
    const adminIds = await detectAdminsByRole();
    
    if (!adminIds || adminIds.length === 0) {
      console.error('❌ No hay administradores disponibles para notificar');
      return { success: 0, failed: 0 };
    }
    
    console.log(`📋 Enviando notificación a ${adminIds.length} administradores`);
    
    let success = 0;
    let failed = 0;
    
    // Enviar notificación a cada administrador
    for (const adminId of adminIds) {
      try {
        await createNotificationFn({
          ...notificationData,
          userId: adminId
        });
        success++;
        console.log(`✅ Notificación enviada a administrador: ${adminId}`);
      } catch (error) {
        failed++;
        console.error(`❌ Error enviando notificación a ${adminId}:`, error);
      }
    }
    
    console.log(`📊 Resultado: ${success} notificaciones enviadas, ${failed} fallidas`);
    return { success, failed };
  } catch (error) {
    console.error('❌ Error en notifyAllAdmins:', error);
    return { success: 0, failed: 1 };
  }
};

/**
 * Configura un listener para detectar cambios en los usuarios con rol "admin"
 * @param {Function} callback - Función a llamar cuando cambia la lista de administradores
 * @returns {Function} - Función para cancelar el listener
 */
export const setupAdminRoleListener = (callback) => {
  const usersRef = collection(db, 'users');
  const roleQuery = query(usersRef, where('role', '==', 'admin'));
  
  return onSnapshot(roleQuery, (snapshot) => {
    const adminIds = [];
    snapshot.forEach(doc => {
      adminIds.push(doc.id);
    });
    
    console.log(`🔄 Cambio detectado en administradores. Total: ${adminIds.length}`);
    adminIdsCache = [...adminIds];
    adminCacheTimestamp = Date.now();
    
    if (typeof callback === 'function') {
      callback(adminIds);
    }
  }, (error) => {
    console.error('❌ Error en listener de administradores:', error);
  });
};

/**
 * Verifica si un usuario tiene rol de administrador
 * @param {string} userId - ID del usuario a verificar
 * @returns {Promise<boolean>} - true si es administrador, false si no
 */
export const hasAdminRole = async (userId) => {
  if (!userId) return false;
  
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.role === 'admin' || userData.isAdmin === true;
    }
    
    return false;
  } catch (error) {
    console.error('❌ Error verificando rol de administrador:', error);
    return false;
  }
};

/**
 * Asegura que un usuario tenga rol de administrador
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - true si se configuró correctamente, false si no
 */
export const ensureAdminRole = async (userId) => {
  if (!userId) return false;
  
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Si ya es admin, no hacer nada
      if (userData.role === 'admin') {
        console.log(`✅ Usuario ${userId} ya tiene rol admin`);
        return true;
      }
      
      // Actualizar rol a admin
      await updateDoc(userRef, {
        role: 'admin',
        isAdmin: true,
        updatedAt: new Date()
      });
      
      console.log(`✅ Usuario ${userId} actualizado a rol admin`);
      return true;
    } else {
      console.error(`❌ Usuario ${userId} no existe en la base de datos`);
      return false;
    }
  } catch (error) {
    console.error('❌ Error configurando rol de administrador:', error);
    return false;
  }
};

// Exportar función global para uso en consola
window.ensureAdminRole = ensureAdminRole;
window.detectAdminsByRole = detectAdminsByRole;
